import { PPSTransitionRecord, deletePPSTransitionRecords, getPPSTransitionRecords, updatePPSTransitionRecords } from "@/services/project/PPSTransition.service"
import { getFSContentSelector, getFlowSimulationSelector } from "@/store/flowsheet/flowsheet.slice"
import { getPropertySets } from "@/store/project/project.slice"
import { useRequest } from "ahooks"
import { Button, Dropdown, Table } from "antd"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { TransitionModal } from "./TransitionModal"
import { EllipsisOutlined } from "@ant-design/icons"
import { MapModal } from "./MapModal"

export const PPSTPanel = () => {
  const flowSimulation = useSelector(getFlowSimulationSelector)
  const {data, run} = useRequest((sId: number) => getPPSTransitionRecords(sId), {manual: true})
  const projectPPS = useSelector(getPropertySets) || []
  const fsContent = useSelector(getFSContentSelector)
  const [editRecord, setEditRecord] = useState<PPSTransitionRecord | null>(null)
  const [mapRecord, setMapRecord] = useState<PPSTransitionRecord | null>(null)

  const fsContentMap = useMemo(() => {
    const unitsMap = {}
    fsContent.sheets.forEach((sheet) => {
      sheet.nodes.forEach(node => {
        unitsMap[node.id] = {
          id: node.id,
          name: node.tag
        }
      })
      sheet.lines.forEach(line => {
        unitsMap[line.id] = {
          id: line.id,
          name: line.tag
        }
      })
    })
    return unitsMap;
  }, [fsContent])

  const ppsMap = useMemo(() => {
    const map = {}
    projectPPS.forEach(item => {
      map[item.id] = item;
    })
    return map;
  }, [projectPPS])

  const transitions = useMemo(() => {
    if (!data) return []
    return data.map((item) => {
      return {
        ...item,
        stream: fsContentMap[item.stream] || {},
      }
    })
  }, [data, fsContentMap, ppsMap])

  const getList = useCallback(() => {
    run(flowSimulation!.id)
  }, [flowSimulation])

  useEffect(() => {
    if (!flowSimulation) {
      return
    }
    run(flowSimulation!.id)
  }, [flowSimulation])

  const actions = [
    {
      label: 'Map',
      key: 'map',
      supportInput: false
    },
    {
      label: 'Edit',
      key: 'edit',
      supportInput: true
    },
    {
      label: 'Delete',
      key: 'delete',
      danger: true,
      supportInput: true
    },
  ]

  const handleMenuClick = async (info, record) => {
    const {key} = info;
    const orginRecord = {
      ...record,
      stream: record.stream.id,
    }
    if (key === 'map') {
      setMapRecord(orginRecord)
    } else if (key === 'edit') {
      delete orginRecord.equipment
      setEditRecord(orginRecord)

    } else if (key === 'delete') {
      await deletePPSTransitionRecords(record.stream.id, flowSimulation!.id)
      getList()
    }
  }

  const columns = [
    {
      title: 'Streams',
      dataIndex: 'stream',
      render: (stream) => {
        return stream.name
      }
    },
    {
      title: 'Property Set',
      dataIndex: 'pps',
      render: (pps: string) => {
        return pps
      }
    },
    {
      title: 'Transition',
      dataIndex: 'isTransition',
      render: (isTransition) => {
        return isTransition ? 'Yes' : 'No'
      }
    },
    {
      title: 'Actions',
      render: (pps, record) => {
        const rowActions = record.isTransition ? actions : actions.filter(item => item.supportInput);
        return <Dropdown
          menu={{
            items: rowActions,
            onClick: (info) => {
              handleMenuClick(info, record)
            },
          }}
        >
          <Button icon={<EllipsisOutlined />}/>
        </Dropdown>
      }
    },

  ]

  const onMapSave = async (changes) => {
    const newRecord = {
      ...mapRecord,
      map: changes
    }
    await updatePPSTransitionRecords(newRecord, {id: newRecord.stream, sId: flowSimulation!.id})
    setMapRecord(null)
    getList()
  }
  
  if (!flowSimulation) {
    return null
  }

  return <div className="unit-container">
    <div className={'unit-header flex-space-btw'}>
      <div>Physical Property Set Selections</div>
    </div>
    <div className="p-1">
      <div className="pt-1 pb-1 flex-end">
        <TransitionModal
          trigger={<Button type="primary">Create</Button>}
          simId={flowSimulation.id}
          onSuccess={getList}
        />
      </div>
      <Table columns={columns} dataSource={transitions} size="small" bordered />
    </div>
    {editRecord && <TransitionModal record={editRecord} simId={flowSimulation.id} onCancel={() => {
      setEditRecord(null)
    }} onSuccess={() => {
      setEditRecord(null)
      getList()
    }} />}

    {mapRecord && <MapModal
      record={mapRecord}
      value={mapRecord.map || []}
      onChange={onMapSave}
      onCancel={() => {
        setMapRecord(null)
      }}
    />}
  </div>
}